"use client";

import * as React from "react";
import { cn } from "@/lib/utils";
import * as DialogPrimitive from "@radix-ui/react-dialog";

function Sheet({ ...props }: React.ComponentProps<typeof DialogPrimitive.Root>) {
  return <DialogPrimitive.Root {...props} />;
}

function SheetTrigger({ ...props }: React.ComponentProps<typeof DialogPrimitive.Trigger>) {
  return <DialogPrimitive.Trigger {...props} />;
}

function SheetClose({ ...props }: React.ComponentProps<typeof DialogPrimitive.Close>) {
  return <DialogPrimitive.Close {...props} />;
}

function SheetPortal({ ...props }: React.ComponentProps<typeof DialogPrimitive.Portal>) {
  return <DialogPrimitive.Portal {...props} />;
}

function SheetOverlay({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Overlay>) {
  return (
    <DialogPrimitive.Overlay
      className={cn(
        "fixed inset-0 z-50 bg-black/50 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
        className,
      )}
      {...props}
    />
  );
}

interface SheetContentProps extends React.ComponentProps<typeof DialogPrimitive.Content> {
  side?: "top" | "bottom" | "left" | "right";
  size?: "default" | "full" | "auto";
  showHandle?: boolean;
}

function SheetContent({
  side = "bottom",
  size = "default",
  showHandle = true,
  className,
  children,
  ...props
}: SheetContentProps) {
  const sizeClasses = {
    default: "h-[60vh]",
    full: "h-[90vh]",
    auto: "h-auto max-h-[90vh]",
  };

  const sideClasses = {
    top: "top-0 inset-x-0 rounded-b-large data-[state=closed]:slide-out-to-top data-[state=open]:slide-in-from-top",
    bottom:
      "bottom-0 inset-x-0 rounded-t-large data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom",
    left: "left-0 inset-y-0 w-3/4 rounded-r-large data-[state=closed]:slide-out-to-left data-[state=open]:slide-in-from-left",
    right:
      "right-0 inset-y-0 w-3/4 rounded-l-large data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right",
  };

  return (
    <SheetPortal>
      <SheetOverlay />
      <DialogPrimitive.Content
        className={cn(
          "fixed z-drawer flex flex-col gap-4 border border-cede-border-container bg-cede-bg-drawer backdrop-blur-xl shadow-lg transition ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out",
          // Use the shared --panel-* tokens so all drawer/panel motion stays in sync.
          "data-[state=open]:duration-[var(--panel-open-dur)] data-[state=closed]:duration-[var(--panel-close-dur)] data-[state=open]:ease-[var(--panel-ease)] data-[state=closed]:ease-[var(--panel-ease)]",
          sideClasses[side],
          sizeClasses[size],
          className,
        )}
        {...props}
      >
        {/* Handle Bar for bottom/top sheets */}
        {showHandle && (side === "bottom" || side === "top") && (
          <div className="mx-auto mt-2 h-1 w-12 rounded-full bg-cede-border-container" />
        )}
        {children}
      </DialogPrimitive.Content>
    </SheetPortal>
  );
}

function SheetHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col gap-2 text-center sm:text-left px-6", className)} {...props} />;
}

function SheetFooter({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end px-6", className)} {...props} />;
}

function SheetTitle({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Title>) {
  return <DialogPrimitive.Title className={cn("text-lg font-semibold text-cede-text-primary", className)} {...props} />;
}

function SheetDescription({ className, ...props }: React.ComponentProps<typeof DialogPrimitive.Description>) {
  return <DialogPrimitive.Description className={cn("text-sm text-cede-text-secondary", className)} {...props} />;
}

export { Sheet, SheetTrigger, SheetClose, SheetContent, SheetHeader, SheetFooter, SheetTitle, SheetDescription };
